import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngxs/store";
import { OrganizationState } from "@vp/data-access/organization";
import { TagsState } from "@vp/data-access/tags";
import { Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { TagSelector, TagSelectorState } from "./state/tag-selector-state";
import * as TagSelectorActions from "./state/tag-selector-state.actions";

export interface TagSelection {
  tagTypeFriendlyId: string;
  tagId: string;
}

@Injectable()
export class TagSelectorContext implements OnDestroy {
  selectors$: Observable<TagSelector[]>;

  private readonly _destroyed$ = new Subject<void>();
  private key = "";

  constructor(private readonly store: Store) {
    this.selectors$ = this.store
      .select(TagSelectorState.selectors)
      .pipe(map(selectors => selectors.filter(selector => selector.key === this.key)));
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  setSelectorKey(key: string) {
    this.key = key;
  }

  getSelectedIds = () =>
    this.store
      .selectSnapshot(TagSelectorState.selectors)
      .filter(s => s.selectedTag !== null)
      .map((selector: TagSelector) => (selector.selectedTag ? selector.selectedTag.tagId : null))
      .filter((tagId): tagId is string => tagId !== null);

  initailizeSelectors = (key: string, tagIds: string[] = []) => {
    this.setSelectorKey(key);
    if (tagIds?.length === 0) return;

    tagIds.forEach(tagId => {
      const tag = this.store.selectSnapshot(TagsState.tags).find(tag => tag.tagId === tagId);

      if (!tag) return;

      const tagType = this.store
        .selectSnapshot(OrganizationState.tagTypes)
        .find(tagType => tagType.friendlyId === tag.tagTypeFriendlyId);

      const accessibleTags = this.store
        .selectSnapshot(TagsState.filtered)
        .filter(t => t.tagTypeFriendlyId === tag.tagTypeFriendlyId);

      const selector = {
        key: this.key,
        tagType: tagType,
        selectedTag: tag,
        tags: accessibleTags
      } as TagSelector;

      this.store.dispatch(new TagSelectorActions.AddSelector(selector));
    });
  };

  //addSelector = (tagTypeFriendlyId: string, selectedTagId: string | undefined | null) => {
  addSelector = (tagTypeFriendlyId: string) => {
    const tagType = this.store
      .selectSnapshot(OrganizationState.tagTypes)
      .find(tagType => tagType.friendlyId === tagTypeFriendlyId);

    if (!tagType) return;

    const tagsOfType = this.store
      .selectSnapshot(TagsState.filtered)
      .filter(tag => tag.tagTypeFriendlyId === tagTypeFriendlyId);
    //const selected = tagsOfType.find(t => t.tagId === selectedTagId);

    this.store.dispatch(
      new TagSelectorActions.AddSelector({
        key: this.key,
        tagType: tagType,
        tags: tagsOfType,
        selectedTag: null,
        fixed: false
      })
    );
  };

  selectTagFor = (tagTypeFriendlyId: string, tagId: string) => {
    const selectors = this.store.selectSnapshot(TagSelectorState.getSelectors);
    const currentSelector = selectors.find(
      s => s.tagType.friendlyId == tagTypeFriendlyId && s.key === this.key
    );
    const tag = this.store.selectSnapshot(TagsState.tags).find(tag => tag.tagId === tagId);

    if (!currentSelector) return;

    if (currentSelector && !tag) {
      this.store.dispatch(
        new TagSelectorActions.PatchSelector(
          currentSelector.tagType.friendlyId,
          {
            selectedTag: null
          },
          this.key
        )
      );
    }

    const currentLevel = currentSelector.tagType.tagTypeFriendlyPathId
      ? currentSelector.tagType.tagTypeFriendlyPathId.split(".").length
      : 0;

    selectors.forEach(selector => {
      // Determine the hierarchy level of each selector
      const selectorLevel = selector.tagType.tagTypeFriendlyPathId
        ? selector.tagType.tagTypeFriendlyPathId.split(".").length
        : 0;

      if (selector.tagType.friendlyId === tagTypeFriendlyId) {
        // Update the current selector with the selected tag
        this.store.dispatch(
          new TagSelectorActions.PatchSelector(
            tagTypeFriendlyId,
            {
              selectedTag: tag
            },
            this.key
          )
        );
      } else if (
        selectorLevel > currentLevel &&
        selector.tagType.tagTypeFriendlyPathId.includes(currentSelector.tagType.friendlyId)
      ) {
        // Nullify the selectedTag for selectors at a deeper level in the hierarchy
        this.store.dispatch(
          new TagSelectorActions.PatchSelector(
            selector.tagType.friendlyId,
            {
              selectedTag: null
            },
            this.key
          )
        );
      }
    });
  };
}
